import { UserPlus } from 'lucide-react';
import { useStaffDashboard } from '../hooks';
import { DataView } from '../../../components/ui/DataView';
import { Card } from '../../../components/ui/Card';
import type { PeriodOption } from '@repo/shared-dashboard';

interface NewMembersWidgetProps {
  period?: PeriodOption;
}

const periodBadges: Record<PeriodOption, string> = {
  today: 'Today',
  week: 'By Day',
  month: 'By Day',
  quarter: 'By Month',
  year: 'By Month',
};

const periodFooters: Record<PeriodOption, string> = {
  today: 'Member onboarding recorded today',
  week: 'Member onboarding recorded this week',
  month: 'Member onboarding recorded this month',
  quarter: 'Member onboarding recorded this quarter',
  year: 'Member onboarding recorded this year',
};

export function NewMembersWidget({ period = 'month' }: NewMembersWidgetProps) {
  const query = useStaffDashboard(period);
  const badgeLabel = periodBadges[period] ?? 'By Month';
  const footerLabel = periodFooters[period] ?? 'Data generated over past months';

  return (
    <Card className="flex flex-col justify-between overflow-hidden h-full">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">New Members</h3>
        <span className="text-xs bg-surface-2 text-text-secondary px-2.5 py-1 rounded-[6px] border border-border">
          {badgeLabel}
        </span>
      </div>
      <DataView query={query} isEmpty={() => false}>
        {(data) => {
          const list = data?.newMembersByMonth ?? [];
          if (list.length === 0) {
            return (
              <div className="p-8 flex flex-col items-center justify-center text-center my-auto">
                <div className="w-10 h-10 rounded-full bg-surface-2 flex items-center justify-center text-text-muted mb-2.5">
                  <UserPlus className="w-5 h-5 opacity-70" />
                </div>
                <p className="text-sm font-semibold text-text-primary">No Registrations in Period</p>
                <p className="text-xs text-text-muted mt-1 max-w-[220px]">
                  Member onboarding figures for this period will display here
                </p>
              </div>
            );
          }

          return (
            <div className="divide-y divide-border overflow-y-auto" style={{ maxHeight: '260px' }}>
              {list.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-3.5 hover:bg-surface-2 transition">
                  <span className="text-sm text-text-secondary">{item.month}</span>
                  <span className="text-sm font-semibold tnum text-text-primary bg-surface-2 px-2.5 py-1 rounded-[6px]">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          );
        }}
      </DataView>
      <div className="p-4 bg-surface-2/60 border-t border-border text-center text-xs text-text-muted">
        {footerLabel}
      </div>
    </Card>
  );
}

